import { DownloadIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Link, Text } from "@chakra-ui/react";
import { MouseEvent, useContext } from "react";
import { FormattedMessage } from "react-intl";

import { MetaModelsContext, Mode, templateDownloadUrl } from "@nens/ldo/api-v1";
import { ApiFetchFunctionBareResponse, LdoAuthContext } from "@nens/ldo/auth-v1";
import { useActionAllowed } from "@nens/ldo/hooks";
import { downloadData } from "@nens/shared-utils";

interface DownloadSheetProps {
  setMode(mode: Mode): void;
  done: boolean;
  mode: Mode | null;
}

interface TemplateDownloadSheetProps {
  latestMetaModelId: number;
  scenarioType: string;
  scenarioTypeDescription: string;
}

const doDownloadTemplate = async (
  e: MouseEvent,
  latestMetaModelId: number,
  excelFilename: string,
  apiFetchFunctionBareResponse: ApiFetchFunctionBareResponse,
  scenarioType?: string
) => {
  e.preventDefault();
  const response = await apiFetchFunctionBareResponse(
    templateDownloadUrl(latestMetaModelId, scenarioType)
  );
  if (response !== null) {
    const data = await response.blob();
    await downloadData(data, excelFilename);
  }
};

export function TemplateDownloadSheet({
  latestMetaModelId,
  scenarioType,
  scenarioTypeDescription,
}: TemplateDownloadSheetProps) {
  const excelFilename = `import_scenarios_${scenarioType}.xlsx`;
  const { apiFetchFunctionBareResponse } = useContext(LdoAuthContext);
  return (
    <Box m={4}>
      <Text m={4}>
        <FormattedMessage
          id="steps.download.explanation.template"
          defaultMessage="In order to download the template containing only the relevant attributes for type {scenarioType} ({scenarioTypeDescription}), click on the download link below:"
          values={{
            scenarioType,
            scenarioTypeDescription,
          }}
        />
      </Text>
      <Link
        href={templateDownloadUrl(latestMetaModelId, scenarioType)}
        fontWeight="semibold"
        onClick={(e) =>
          doDownloadTemplate(
            e,
            latestMetaModelId,
            excelFilename,
            apiFetchFunctionBareResponse,
            scenarioType
          )
        }
      >
        {excelFilename}
        <DownloadIcon mx="6px" />
      </Link>
    </Box>
  );
}

export function DownloadSheet({ setMode, done, mode }: DownloadSheetProps) {
  const actionAllowed = useActionAllowed(null);
  const { getMetaModelById, latestMetaModelId } = useContext(MetaModelsContext);
  const { apiFetchFunctionBareResponse } = useContext(LdoAuthContext);
  const templateLastUpdated = getMetaModelById(latestMetaModelId).updated_at.toLocaleDateString();

  if (done) {
    return (
      <Text m={4}>
        {mode === "create" ? (
          <FormattedMessage
            id="steps.import.importing_new_scenarios"
            defaultMessage="Importing new scenarios"
          />
        ) : (
          <FormattedMessage
            id="steps.import.updating_existing_scenarios"
            defaultMessage="Updating existing scenarios"
          />
        )}
      </Text>
    );
  } else {
    return (
      <>
        <Box bgColor="green.50" p="20px" rounded="lg">
          <Text m={4}>
            <FormattedMessage
              id="steps.download.explanation"
              defaultMessage="Importing scenarios works by filling in an Excel sheet with all the scenarios' details and uploading it. Please start by downloading one of the following templates that you can then fill in."
            />
          </Text>
          <Box m={4}>
            <Text m={4}>
              <FormattedMessage
                id="steps.download.explanation_master"
                defaultMessage="In order to download the full template, including all attributes, click on the download link below:"
              />
            </Text>
            <Link
              href={templateDownloadUrl(latestMetaModelId, undefined)}
              fontWeight="semibold"
              onClick={(e) =>
                doDownloadTemplate(
                  e,
                  latestMetaModelId,
                  "import_scenarios.xlsx",
                  apiFetchFunctionBareResponse
                )
              }
            >
              import_scenarios.xlsx
              <DownloadIcon mx="6px" />
            </Link>
          </Box>
          <TemplateDownloadSheet
            latestMetaModelId={latestMetaModelId}
            scenarioType={"A"}
            scenarioTypeDescription={"primair onbeschermd"}
          />
          <TemplateDownloadSheet
            latestMetaModelId={latestMetaModelId}
            scenarioType={"B"}
            scenarioTypeDescription={"doorbraak primaire kering"}
          />
          <TemplateDownloadSheet
            latestMetaModelId={latestMetaModelId}
            scenarioType={"C"}
            scenarioTypeDescription={"doorbraak niet-primaire kering"}
          />
          <TemplateDownloadSheet
            latestMetaModelId={latestMetaModelId}
            scenarioType={"D"}
            scenarioTypeDescription={"regionaal onbeschermd"}
          />
          <TemplateDownloadSheet
            latestMetaModelId={latestMetaModelId}
            scenarioType={"bovenregionaal"}
            scenarioTypeDescription={"stresstest wateroverlast"}
          />
          <Text m={4}>
            <FormattedMessage
              id="steps.download.metamodel_version"
              defaultMessage="These templates uses version {value} of the metadata definition and are last updated on {last_updated}."
              values={{ value: latestMetaModelId, last_updated: templateLastUpdated }}
            />
          </Text>
        </Box>
        <Text m={4}>
          <WarningTwoIcon color="orange.300" mx="6px" w="22px" h="22px" />
          <FormattedMessage
            id="steps.download.update_explanation"
            defaultMessage="<b>Alternatively,</b> it is possible to update <i>existing</i> scenario metadata by uploading an Excel sheet with updated data. Uploading new result files this way is <i>not</i> supported."
            values={{
              b: (chunks) => (
                <Text as="strong" fontWeight="bold">
                  {chunks}
                </Text>
              ),
              i: (chunks) => (
                <Text as="span" fontStyle="italic">
                  {chunks}
                </Text>
              ),
            }}
          />
        </Text>
        <Flex alignItems="center" flexDirection={{ sm: "column", md: "column", lg: "row" }}>
          <Button m={4} onClick={() => setMode("create")} isDisabled={!actionAllowed("create")}>
            <FormattedMessage
              id="steps.download.import_new_button"
              defaultMessage="Import NEW scenarios using Excel and ZIP files"
            />
          </Button>
          <Button
            variant="outline"
            m={4}
            onClick={() => setMode("update")}
            isDisabled={!actionAllowed("bulk update")}
          >
            <FormattedMessage
              id="steps.download.import_existing_button"
              defaultMessage="Update EXISTING scenario metadata using Excel file only"
            />
          </Button>
        </Flex>
      </>
    );
  }
}
